import React, { useState, useEffect, useCallback } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { TodoTemplateCard } from "../../components/CompanyAdmin/TodoTemplate/TodoTemplateCard";
import { TodoTemplateDialog } from "../../components/CompanyAdmin/TodoTemplate/TodoTemplateDialog";
import { Button } from "../../components/UI/Button";
import { useAuthStore } from "../../stores/auth-store";
import { todoTemplateUseCase, companyProductUseCase, companyUseCase } from "../../../di/container";
import type {
  TodoTemplate,
  CreateTodoTemplateRequest,
  UpdateTodoTemplateRequest,
} from "../../../domain/types/todo";
import type { ProductDto } from "../../../domain/types/company/company-product";
import type { UserInfoResponse } from "../../../shared/types/api";
import { Plus, Package, AlertCircle, Loader2, X as XIcon } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableTemplateCardProps {
  template: TodoTemplate;
  onEdit: (template: TodoTemplate) => void;
  onDelete: (template: TodoTemplate) => void;
  onToggleActive: (template: TodoTemplate) => void;
}

const SortableTemplateCard: React.FC<SortableTemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: template.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TodoTemplateCard
        template={template}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleActive={onToggleActive}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

const TodoTemplateManagement: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  // State
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<TodoTemplate[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserInfoResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TodoTemplate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation state
  const [deleteConfirmTemplate, setDeleteConfirmTemplate] = useState<TodoTemplate | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!companyId) return;

      setIsProductsLoading(true);
      try {
        const productData = await companyProductUseCase.getProducts(companyId);

        // API 응답이 배열인 경우와 categories를 포함하는 객체인 경우 모두 처리
        let allProducts: ProductDto[];
        if (Array.isArray(productData)) {
          // 새 API 형식: List<ProductEntity> 직접 반환
          allProducts = productData as unknown as ProductDto[];
        } else if (productData && 'categories' in productData) {
          // 기존 API 형식: { categories: [...] }
          allProducts = productData.categories.flatMap(
            (cat) => cat.products as unknown as ProductDto[]
          );
        } else {
          allProducts = [];
        }

        setProducts(allProducts);

        // Auto-select first product
        if (allProducts.length > 0 && !selectedProductId) {
          setSelectedProductId(allProducts[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError("상품 목록을 불러오는데 실패했습니다.");
      } finally {
        setIsProductsLoading(false);
      }
    };

    fetchProducts();
  }, [companyId]);

  // Fetch company users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!companyId) return;

      try {
        const usersResponse = await companyUseCase.getUserByCompanyId({
          companyId,
          pageable: { page: 0, size: 100 },
        });
        setCompanyUsers(usersResponse.items || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    fetchUsers();
  }, [companyId]);

  // Fetch templates when product changes
  const fetchTemplates = useCallback(async () => {
    if (!companyId || !selectedProductId) return;

    setIsLoading(true);
    setError(null);

    try {
      const templateData = await todoTemplateUseCase.getTemplates(
        companyId,
        selectedProductId
      );
      // Ensure templateData is an array and sort by displayOrder
      const templatesArray = Array.isArray(templateData) ? templateData : [];
      const sortedTemplates = [...templatesArray].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      );
      setTemplates(sortedTemplates);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError("작업 공정 목록을 불러오는데 실패했습니다. (API가 구현되지 않았을 수 있습니다)");
      setTemplates([]); // Reset to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [companyId, selectedProductId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Handlers
  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setIsDialogOpen(true);
  };

  const handleEditTemplate = (template: TodoTemplate) => {
    setEditingTemplate(template);
    setIsDialogOpen(true);
  };

  const handleDeleteTemplate = (template: TodoTemplate) => {
    setDeleteConfirmTemplate(template);
  };

  const confirmDelete = async () => {
    if (!companyId || !selectedProductId || !deleteConfirmTemplate) return;

    try {
      await todoTemplateUseCase.deleteTemplate(
        companyId,
        selectedProductId,
        deleteConfirmTemplate.id
      );
      setTemplates((prev) =>
        prev.filter((t) => t.id !== deleteConfirmTemplate.id)
      );
      setDeleteConfirmTemplate(null);
    } catch (err) {
      console.error("Failed to delete template:", err);
      setError("작업 공정 삭제에 실패했습니다.");
    }
  };

  const handleToggleActive = async (template: TodoTemplate) => {
    if (!companyId || !selectedProductId) return;

    try {
      const updated = await todoTemplateUseCase.toggleActive(
        companyId,
        selectedProductId,
        template.id
      );
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? updated : t))
      );
    } catch (err) {
      console.error("Failed to toggle active:", err);
      setError("활성화 상태 변경에 실패했습니다.");
    }
  };

  const handleDialogSubmit = async (
    request: CreateTodoTemplateRequest | UpdateTodoTemplateRequest
  ) => {
    if (!companyId || !selectedProductId) return;

    setIsSubmitting(true);
    try {
      if (editingTemplate) {
        const updated = await todoTemplateUseCase.updateTemplate(
          companyId,
          selectedProductId,
          editingTemplate.id,
          request
        );
        setTemplates((prev) => {
          const newTemplates = prev.map((t) => (t.id === editingTemplate.id ? updated : t));
          return [...newTemplates].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        });
      } else {
        const created = await todoTemplateUseCase.createTemplate(
          companyId,
          selectedProductId,
          request as CreateTodoTemplateRequest
        );
        setTemplates((prev) => {
          const newTemplates = [...prev, created];
          return [...newTemplates].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        });
      }
      setIsDialogOpen(false);
      setEditingTemplate(null);
    } catch (err) {
      console.error("Failed to save template:", err);
      setError("작업 공정 저장에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !companyId || !selectedProductId) {
      return;
    }

    const oldIndex = templates.findIndex((t) => t.id === active.id);
    const newIndex = templates.findIndex((t) => t.id === over.id);

    const reordered = arrayMove(templates, oldIndex, newIndex);
    setTemplates(reordered);

    try {
      const updatedTemplates = await todoTemplateUseCase.reorderTemplates(companyId, selectedProductId, {
        templateIds: reordered.map((t) => t.id),
      });
      // 서버에서 반환된 정렬된 데이터로 업데이트
      if (Array.isArray(updatedTemplates) && updatedTemplates.length > 0) {
        const sortedTemplates = [...updatedTemplates].sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        );
        setTemplates(sortedTemplates);
      }
    } catch (err) {
      console.error("Failed to reorder templates:", err);
      // Revert on error
      setTemplates(templates);
      setError("순서 변경에 실패했습니다.");
    }
  };

  return (
    <CompanyAdminLayout>
      <div className="p-5">
        {/* Header */}
        <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
          <h2 className="text-[1.375rem] font-semibold">작업 공정 관리</h2>
        </div>

        {/* Product Selector & Add Button */}
        <div className="mb-[1.25rem] p-[1.25rem] border border-[#d9d9d9] rounded-[0.625rem] bg-white flex flex-col md:flex-row gap-[0.75rem] justify-between items-start md:items-center">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              상품 선택
            </label>
            {isProductsLoading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="animate-spin" size={16} />
                <span>불러오는 중...</span>
              </div>
            ) : products.length > 0 ? (
              <select
                value={selectedProductId || ""}
                onChange={(e) => setSelectedProductId(Number(e.target.value))}
                className="w-[200px] rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-[#4a6cf7] focus:outline-none"
              >
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Package size={16} />
                <span>등록된 상품이 없습니다.</span>
              </div>
            )}
          </div>
          {selectedProductId && (
            <Button
              onClick={handleAddTemplate}
              className="h-[2.5rem] px-4 bg-[#4a6cf7] text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <Plus size={16} />
              공정 추가
            </Button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-[1.25rem] p-[0.75rem] border border-red-200 rounded-[0.625rem] bg-red-50 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle size={16} />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <XIcon size={16} />
            </button>
          </div>
        )}

        {/* Templates List */}
        {selectedProductId && (
          <div className="border border-[#d9d9d9] rounded-[0.625rem] bg-white">
            <div className="p-[1rem] border-b border-[#d9d9d9]">
              <h3 className="font-medium text-gray-900 text-sm">작업 공정 목록</h3>
              <p className="text-xs text-gray-500 mt-1">
                드래그하여 순서를 변경할 수 있습니다.
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                <Loader2 className="animate-spin mr-2" size={18} />
                <span>불러오는 중...</span>
              </div>
            ) : templates.length > 0 ? (
              <div className="p-[1rem]">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={templates.map((t) => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {templates.map((template) => (
                        <SortableTemplateCard
                          key={template.id}
                          template={template}
                          onEdit={handleEditTemplate}
                          onDelete={handleDeleteTemplate}
                          onToggleActive={handleToggleActive}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Package size={40} className="mb-2 opacity-50" />
                <p className="text-sm">등록된 작업 공정이 없습니다.</p>
                <button
                  onClick={handleAddTemplate}
                  className="mt-3 text-sm text-[#4a6cf7] hover:underline"
                >
                  첫 번째 공정 추가하기
                </button>
              </div>
            )}
          </div>
        )}

        {/* Template Dialog */}
        <TodoTemplateDialog
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingTemplate(null);
          }}
          onSubmit={handleDialogSubmit}
          template={editingTemplate}
          existingTemplates={templates}
          companyUsers={companyUsers}
          isLoading={isSubmitting}
        />

        {/* Delete Confirmation Modal */}
        {deleteConfirmTemplate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-[340px] rounded-lg bg-white p-5 shadow-lg">
              <h3 className="mb-4 text-base font-semibold text-left">
                작업 공정 삭제
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                "{deleteConfirmTemplate.name}" 공정을 삭제하시겠습니까?
              </p>
              <p className="text-xs text-red-600 mb-4">
                이 작업은 되돌릴 수 없습니다.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmTemplate(null)}
                  className="h-[2.25rem] px-3 text-xs"
                >
                  취소
                </Button>
                <Button
                  onClick={confirmDelete}
                  className="h-[2.25rem] px-3 bg-red-500 text-white text-xs"
                >
                  삭제
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyAdminLayout>
  );
};

export default TodoTemplateManagement;
